/**
 * chipDom.ts — pure DOM/string helpers for var-chip handling.
 * No React, no JSX. Unit-testable in isolation.
 */

/**
 * unchip — converts var-chip spans back to ${varName} template tokens.
 *
 * Input:  innerHTML containing <span class="var-chip" … data-var="x">…</span>
 * Output: text with ${x} in place of each chip.
 *
 * The regex is intentionally non-greedy and handles sample/non-sample variants:
 *   <span class="var-chip" data-var="x" …>label</span>  →  ${x}
 *   <span class="var-chip sample" data-var="x" …>…</span>  →  ${x}
 *
 * Any class attribute ordering is tolerated as long as "var-chip" appears first
 * (which is what renderTextWithVarsEditable produces).
 */
export function unchip(html: string): string {
	if (!html) return "";
	// Match <span …> that carries class="var-chip…" and data-var="NAME"
	// The data-var attribute may appear before or after other attributes.
	return String(html).replace(/<span[^>]*?\bdata-var="([^"]+)"[^>]*?>[\s\S]*?<\/span>/g, "${$1}");
}

/**
 * decodeEntities — converts innerHTML artifacts back to plain text for storage.
 * contentEditable represents spaces as &nbsp; and escapes &/</> — storing that raw
 * makes "&nbsp;" show up literally in the canvas/email after the next render.
 * Also normalizes <br> to newline. &amp; is decoded LAST so "&amp;nbsp;" → "&nbsp;"
 * stays a literal user string rather than double-decoding into a space.
 */
export function decodeEntities(s: string): string {
	if (!s) return "";
	return String(s)
		.replace(/<br\s*\/?>/gi, "\n")
		.replace(/&nbsp;/g, " ")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/&amp;/g, "&");
}

/**
 * buildChipHtml — builds the HTML string for a single atomic var-chip.
 * Used when inserting chips programmatically (e.g. via var-menu).
 */
export function buildChipHtml(varName: string, displayText: string, sample: boolean): string {
	const cls = "var-chip" + (sample ? " sample" : "");
	return `<span class="${cls}" contenteditable="false" data-var="${varName}" title="\${${varName}}">${displayText}</span>`;
}
